import { useEffect, useRef, useState } from 'react';

interface UseIntersectionObserverProps {
  threshold?: number;      // How much of element must be visible (0-1)
  rootMargin?: string;     // Margin around viewport (e.g., "100px" triggers earlier)
  triggerOnce?: boolean;   // Only trigger once, or every time it enters viewport
}

/**
 * useIntersectionObserver: Custom React hook that detects when element enters viewport
 * Uses browser's IntersectionObserver API for efficient scroll detection
 * Returns ref to attach to element and isIntersecting boolean
 */
export const useIntersectionObserver = <T extends HTMLElement = HTMLDivElement>({
  threshold = 0.1,         // Default: trigger when 10% visible
  rootMargin = '0px',      // Default: no margin
  triggerOnce = true       // Default: only trigger once
}: UseIntersectionObserverProps = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Create IntersectionObserver: watches when element enters/leaves viewport
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          if (triggerOnce) {
            setHasTriggered(true);  // Mark as triggered so it won't reset
          }
        } else if (!triggerOnce) {
          // Only reset if triggerOnce is false
          setIsIntersecting(false);
        }
      },
      {
        threshold,    // Percentage of element that must be visible
        rootMargin,   // Margin around root (viewport)
      }
    );

    observer.observe(element);

    // Cleanup: stop observing when component unmounts
    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, triggerOnce]);

  // Return ref (attach to element) and intersection state
  // If triggerOnce, return true once triggered, otherwise return current state
  return { ref, isIntersecting: triggerOnce ? hasTriggered || isIntersecting : isIntersecting };
}; 